import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtT } from "./lib";
import CbotStrip from "./CbotStrip";
import { Loader2, FileSignature, AlertTriangle, Layers, Eye, EyeOff } from "lucide-react";

export default function GranosResumen() {
  const [data, setData] = useState(null);
  const [cultivo, setCultivo] = useState("__all__");
  const [loading, setLoading] = useState(true);
  const [incluirFiniquitados, setIncluirFiniquitados] = useState(false);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (cultivo !== "__all__") p.cultivo = cultivo;
    if (incluirFiniquitados) p.incluir_finiquitados = 1;
    api.get("/granos/dashboard", { params: p })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [cultivo, incluirFiniquitados]);

  const cultivos = data?.cultivos || [];

  return (
    <div className="p-4 space-y-4" data-testid="granos-resumen-tab">
      <CbotStrip />
      <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {["__all__", ...cultivos].map((c) => (
          <button key={c} data-testid={`granos-resumen-chip-${c}`} onClick={() => setCultivo(c)}
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium border transition-colors ${
              cultivo === c ? "bg-[#0f5233] text-white border-[#0f5233]" : "bg-white text-slate-600 border-slate-200"
            }`}>
            {c === "__all__" ? "Todos" : c}
          </button>
        ))}
      </div>

      <button onClick={() => setIncluirFiniquitados((v) => !v)} data-testid="granos-resumen-toggle-finiquitados"
        className={`flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium border transition-colors ${
          incluirFiniquitados ? "bg-[#0f5233] text-white border-[#0f5233]" : "bg-white text-slate-600 border-slate-200"
        }`}>
        {incluirFiniquitados ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
        {incluirFiniquitados ? "Mostrando finiquitados" : "Incluir finiquitados"}
      </button>

      {loading || !data ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0f5233]" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl border border-red-100 bg-red-50 p-4" data-testid="granos-kpi-sinfirmar">
              <FileSignature className="h-5 w-5 text-red-500" />
              <p className="mt-2 text-3xl font-extrabold text-red-700 leading-none">{data.sin_firmar.count}</p>
              <p className="mt-1 text-xs font-medium text-slate-600">Sin firmar</p>
              <p className="text-[11px] text-slate-400">{fmtT(data.sin_firmar.toneladas)} t</p>
            </div>
            <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4" data-testid="granos-kpi-atrasados">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <p className="mt-2 text-3xl font-extrabold text-amber-700 leading-none">{data.atrasados.count}</p>
              <p className="mt-1 text-xs font-medium text-slate-600">Atrasados</p>
              <p className="text-[11px] text-slate-400">{fmtT(data.atrasados.toneladas)} t pend.</p>
            </div>
          </div>

          <ResumenTable title="Por cultivo" rows={data.por_cultivo} />
          <ResumenTable title="Por tipo de contrato" rows={data.por_tipo} />
        </>
      )}
    </div>
  );
}

function ResumenTable({ title, rows }) {
  return (
    <div className="rounded-2xl border bg-white overflow-hidden" data-testid={`granos-resumen-${title}`}>
      <div className="bg-[#0f5233] text-white px-4 py-2.5 text-sm font-semibold flex items-center gap-2">
        <Layers className="h-4 w-4 text-lime-300" /> {title}
      </div>
      <div className="divide-y">
        {(rows || []).map((r) => (
          <div key={r.nombre} className="px-4 py-3">
            <div className="flex items-baseline justify-between">
              <span className="font-semibold text-slate-800 text-sm">{r.nombre}</span>
              <span className="text-xs text-slate-400">{r.contratos} contrato(s)</span>
            </div>
            <div className="mt-1.5 grid grid-cols-3 gap-1 text-[11px]">
              <Metric label="Contratada" value={fmtT(r.contratada)} />
              <Metric label="Entregada" value={fmtT(r.entregada)} />
              <Metric label="Pendiente" value={fmtT(r.pendiente)} strong />
              <Metric label="Washout" value={fmtT(r.washout)} />
              <Metric label="Sin C/P" value={fmtT(r.sin_costo_precio)} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const Metric = ({ label, value, strong }) => (
  <div className="rounded-lg bg-slate-50 px-2 py-1.5">
    <p className="text-slate-400">{label}</p>
    <p className={`text-slate-800 ${strong ? "font-bold" : "font-medium"}`}>{value}</p>
  </div>
);
